import Pressable from '@/components/motion/pressable';
import { ROUTES } from '@/app/routes';
import ChangeCommentModal from '@/components/change-comment-modal';
import VbivStatus from '@/components/vbiv-status';
import type { IClient } from '@/entities/client.entity';
import { CalendarClockIcon, CircleDollarSignIcon, ContainerIcon, HashIcon, LayoutGridIcon, MessageCircleIcon, PhoneIcon, UserIcon } from 'lucide-react';
import { memo, useEffect, useMemo, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';

const formatter = buildFormatter(russianStrings);
const money = new Intl.NumberFormat('en-US', { style: 'decimal', maximumFractionDigits: 0 });

const ClientCard = memo(function ClientCard({ client }: { client: IClient }) {
	const [commentText, setCommentText] = useState(client.comment ?? '');

	const navigate = useNavigate();
	function handleCopy(e: MouseEvent, text: string) {
		e.stopPropagation();
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	const phones = useMemo(() => client.phones?.filter(p => p?.number) ?? [], [client.phones]);

	const cards = useMemo(() => client?.cards?.filter(c => c?.number) ?? [], [client.cards]);

	useEffect(() => {
		if (client.comment !== commentText) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setCommentText(client.comment || '');
		}
	}, [client.comment]);

	return (
		<Pressable
			role='button'
			tabIndex={0}
			className='relative p-2 bg-card rounded-md flex flex-col gap-1 border-border border border-l-2 overflow-hidden cursor-pointer active:bg-accent/30'
			style={{
				borderLeftColor: client.isOnline ? 'var(--chart-2)' : 'var(--chart-5)',
			}}
			onClick={() => navigate(ROUTES.client + client.id)}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					navigate(ROUTES.client + client.id);
				}
			}}
		>
			<div className='flex justify-between items-center'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<MessageCircleIcon size={16} className='opacity-40' />
					</div>
					<span className='flex break-all items-center' onClick={e => e.stopPropagation()}>
						{client.comment && (
							<span className='text-sm hover:bg-accent cursor-pointer select-none text-[#e89bff]' onClick={e => handleCopy(e, client.comment || '')}>
								{client.comment}
							</span>
						)}
						<ChangeCommentModal currentComment={client.comment} client={client} />
					</span>
				</span>

				<div onClick={e => e.stopPropagation()}>
					<VbivStatus clientId={client.id} takeStatus={client.takeStatus} takenFrom={client.takenFrom} />
				</div>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center break-all'>
					<div className='w-[18px]'>
						<HashIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, '#' + client.id)}>
						{client.id}
					</span>
				</span>
				<span className='text-sm pl-2'>
					{client.isOnline ? (
						<span className='text-chart-2 flex items-center gap-1'>
							<div className='bg-chart-2 w-2 h-2 animate-pulse rounded-full'></div>Онлайн
						</span>
					) : (
						<span className='text-chart-5'>Офлайн</span>
					)}
				</span>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<ContainerIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, client.buildId)}>
						{client.buildId}
					</span>
					<div className='w-[18px]'>
						<LayoutGridIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, client.sdkVersion)}>
						{client.sdkVersion}
					</span>
				</span>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<UserIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, client.profileNickname)}>
						{client.profileNickname}
					</span>
				</span>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<PhoneIcon size={16} className='opacity-40' />
					</div>
					<div>
						{phones.length ? (
							phones.map((phone, i) => {
								if (!phone.number) return null;
								return (
									<span
										key={`${phone.subId}${i}`}
										className='mr-1 text-sm hover:bg-accent cursor-pointer select-none break-all'
										onClick={e => handleCopy(e, phone.number!)}
									>
										{phone.number}
									</span>
								);
							})
						) : (
							<span className='opacity-60 text-sm '>Нет номеров</span>
						)}
					</div>
				</span>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<CircleDollarSignIcon size={16} className='opacity-40' />
					</div>
					{cards?.length ? (
						<div className='flex gap-2'>
							{cards.map((card, i) => {
								if (!card.balance) return null;
								return (
									<span
										key={`${card.number}${i}`}
										className='mr-1 font-semibold text-sm hover:bg-accent cursor-pointer select-none break-all text-destructive'
										onClick={e => handleCopy(e, card.balance.toString())}
									>
										{money.format(card.balance)}
									</span>
								);
							})}
						</div>
					) : (
						<span className='opacity-60 text-sm '>Нет Балансов</span>
					)}
				</span>
			</div>

			<span className='flex gap-1 items-center'>
				<div className='w-[18px]'>
					<CalendarClockIcon size={16} className='opacity-40' />
				</div>
				<span className='text-sm font-semibold opacity-40 '>Установлен</span>
				<div className=' text-sm text-chart-2 opacity-95'>
					<TimeAgo date={client.createdAt} formatter={formatter} />
				</div>
			</span>
		</Pressable>
	);
});

export default ClientCard;
